import { getBytes, getLines, getMessages } from './parse'
import type { EventSourceMessage } from './parse'

export const EventStreamContentType = 'text/event-stream'

const DefaultRetryInterval = 1000
const LastEventId = 'last-event-id'

export interface FetchEventSourceInit extends RequestInit {
  /**
     * The request headers. Only supports the Record<string,string> format.
     */
  headers?: Record<string, string>,

  /**
     * Called when a response is received. Use this to validate that the response
     * actually matches what you expect (and throw if it doesn't.)
     */
  onopen: (response: Response) => Promise<void>,

  /**
     * Called when a message is received. NOTE: Unlike the default browser
     * EventSource.onmessage, this callback is called for _all_ events,
     * even ones with a custom `event` field.
     */
  onmessage?: (ev: EventSourceMessage) => void;

  /**
     * Called when a response finishes. If you don't expect the server to kill
     * the connection, you can throw an exception here and retry using onerror.
     */
  onclose?: () => void;

  /**
     * Called when there is any error making the request / processing messages /
     * handling callbacks etc. Use this to control the retry strategy: if the
     * error is fatal, rethrow the error inside the callback to stop the entire
     * operation. Otherwise, you can return an interval (in milliseconds) after
     * which the request will automatically retry (with the last-event-id).
     * If this callback is not specified, or it returns undefined, fetchEventSource
     * will treat every error as retriable and will try again after 1 second.
     */
  onerror?: (err: unknown) => number | null | undefined | void,

  /** The Fetch function to use. Defaults to window.fetch */
  fetch?: typeof fetch;
}

export function fetchEventSource(input: RequestInfo, {
  signal: inputSignal,
  headers: inputHeaders,
  onopen,
  onmessage,
  onclose,
  onerror,
  fetch: inputFetch,
  ...rest
}: FetchEventSourceInit) {
  return new Promise<void>((resolve, reject) => {
    // make a copy of the input headers since we may modify it below:
    const headers = { ...inputHeaders }
    if (!headers.accept) {
      headers.accept = EventStreamContentType
    }

    let curRequestController: AbortController
    let retryTimer = 0
    function dispose() {
      window.clearTimeout(retryTimer)
      curRequestController.abort()
    }

    // if the incoming signal aborts, dispose resources and resolve:
    inputSignal?.addEventListener('abort', () => {
      dispose()
      resolve()
    })

    const fetchFn = inputFetch ?? window.fetch
    async function create() {
      curRequestController = new AbortController()
      try {
        const response = await fetchFn(input, {
          ...rest,
          headers,
          signal: curRequestController.signal,
        })

        await onopen(response)

        if (!response.body) {
          throw new Error('Response body is null — SSE requires a readable stream')
        }

        await getBytes(response.body, getLines(getMessages(id => {
          if (id) {
            headers[LastEventId] = id
          } else {
            delete headers[LastEventId]
          }
        }, _retry => {
          // Server retry interval intentionally ignored — retry logic
          // is owned by the caller (sseClient.ts).
        }, onmessage)))

        onclose?.()
        dispose()
        resolve()
      } catch(err) {
        if (!curRequestController.signal.aborted) {
          try {
            const interval = onerror?.(err) ?? DefaultRetryInterval
            window.clearTimeout(retryTimer)
            retryTimer = window.setTimeout(create, interval)
          } catch(innerErr) {
            dispose()
            reject(innerErr)
          }
        }
      }
    }

    create()
  })
}
